import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, MenuItem, Snackbar, Stack, TextField, Tooltip, Typography} from '@mui/material';
import {HiOutlineSearch} from 'react-icons/hi';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import FaqTable from './FaqTable';
import Pagination from '../../common/Pagination';
import onClickFunctionKeys from '../../../utils/pagination';
import {CategoryFilter, findCategoryNames, findCategoryNamesBySubId} from './CategorySelect';
import styles from './styles/faq.module.css';

const inlineLabelSx = {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--gray-darker, #222)',
    whiteSpace: 'nowrap'
};

const menuItemEllipsisSx = {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};

const ACTIVE_ALL = '__ALL__';
const ACTIVE_TRUE = 'true';
const ACTIVE_FALSE = 'false';

const encodeActive = (v) => {
    if (v === true) return ACTIVE_TRUE;
    if (v === false) return ACTIVE_FALSE;
    return ACTIVE_ALL;
};

const decodeActive = (v) => {
    if (v === ACTIVE_TRUE) return true;
    if (v === ACTIVE_FALSE) return false;
    return null;
};

const LEARNING_STATUS_ALL = '__ALL__';
const LEARNING_STATUS_LABELS = {
    PROGRESS: '학습 중',
    COMPLETED: '학습 완료',
    FAILURE: '학습 실패'
};

const encodeLearningStatus = (v) => v || LEARNING_STATUS_ALL;
const decodeLearningStatus = (v) => (v === LEARNING_STATUS_ALL ? null : v);

export default function FaqList({
    keyword, categoryMainId, categorySubId, isActive, status, page,
    listResponse, listLoading, listErrorResponse, pagination,
    categoryTree, handleChange, handleResetFilters,
    onDeleteFaq, deleteLoading, deleteErrorResponse, onDismissDeleteError,
    onRefreshList,
    onBulkUpload, bulkUploadLoading, bulkUploadResponse, bulkUploadErrorResponse, onDismissBulkUploadResult
}) {
    const navigate = useNavigate();

    const [pageNumbers, setPageNumbers] = useState([]);
    const [prevPageNumberLength, setPrevPageNumberLength] = useState(0);

    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);

    const fileInputRef = useRef(null);
    const [bulkUploadSnackbar, setBulkUploadSnackbar] = useState({ open: false, severity: 'success', message: '' });

    const handleClickBulkUpload = () => {
        if (bulkUploadLoading) return;
        fileInputRef.current?.click();
    };

    const handleFileSelected = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        onBulkUpload?.(file);
    };

    useEffect(() => {
        if (bulkUploadResponse) {
            setBulkUploadSnackbar({
                open: true,
                severity: 'success',
                message: '파일 업로드가 완료되었습니다. 목록을 갱신합니다.'
            });
        }
    }, [bulkUploadResponse]);

    useEffect(() => {
        if (bulkUploadErrorResponse) {
            setBulkUploadSnackbar({
                open: true,
                severity: 'error',
                message: '파일 업로드에 실패했습니다. 파일 형식과 내용을 확인해 주세요.'
            });
        }
    }, [bulkUploadErrorResponse]);

    const handleCloseBulkUploadSnackbar = () => {
        setBulkUploadSnackbar((prev) => ({ ...prev, open: false }));
        onDismissBulkUploadResult?.();
    };

    const handleDeleteRequest = (item) => setPendingDelete(item);
    const handleCloseDeleteModal = () => {
        if (deleteLoading) return;
        setPendingDelete(null);
    };
    const handleConfirmDelete = () => {
        if (!pendingDelete) return;
        onDeleteFaq(pendingDelete.id);
    };

    useEffect(() => {
        if (deleteErrorResponse) {
            setPendingDelete(null);
            setDeleteErrorOpen(true);
        }
    }, [deleteErrorResponse]);

    useEffect(() => {
        if (deleteLoading) return;
        setPendingDelete((current) => (current ? null : current));
    }, [deleteLoading]);

    const handleCategoryChange = ({ categoryMainId: m, categorySubId: s }) => {
        if (m !== undefined) handleChange({ target: { name: 'categoryMainId', value: m } });
        if (s !== undefined) handleChange({ target: { name: 'categorySubId', value: s } });
    };

    const handleActiveChange = (e) => {
        handleChange({ target: { name: 'isActive', value: decodeActive(e.target.value) } });
    };

    const handleStatusChange = (e) => {
        handleChange({ target: { name: 'status', value: decodeLearningStatus(e.target.value) } });
    };

    const requestPagination = (nextPage) => {
        handleChange({ target: { name: 'page', value: nextPage } });
    };

    const handleClickPaginationFunctionKeys = (functionKey) => {
        requestPagination(onClickFunctionKeys(functionKey, pageNumbers, pagination?.currentPage ?? page));
    };

    useEffect(() => {
        if (!pagination) return;
        setPageNumbers((prev) => {
            setPrevPageNumberLength(prev.length);
            return Array.from({ length: pagination.totalPages }, (_, i) => i + 1);
        });
    }, [pagination]);

    const selectedChips = useMemo(() => {
        const chips = [];
        if (categoryMainId) {
            const { mainName } = findCategoryNames(categoryTree, categoryMainId, null);
            chips.push({
                key: 'main',
                label: `1차: ${mainName || categoryMainId}`,
                onDelete: () => {
                    handleChange({ target: { name: 'categoryMainId', value: null } });
                    handleChange({ target: { name: 'categorySubId', value: null } });
                }
            });
        }
        if (categorySubId) {
            const { subName } = findCategoryNamesBySubId(categoryTree, categorySubId);
            chips.push({
                key: 'sub',
                label: `2차: ${subName || categorySubId}`,
                onDelete: () => handleChange({ target: { name: 'categorySubId', value: null } })
            });
        }
        if (isActive === true || isActive === false) {
            chips.push({
                key: 'active',
                label: `노출 상태: ${isActive ? '활성화' : '비활성화'}`,
                onDelete: () => handleChange({ target: { name: 'isActive', value: null } })
            });
        }
        if (status && LEARNING_STATUS_LABELS[status]) {
            chips.push({
                key: 'status',
                label: `학습 상태: ${LEARNING_STATUS_LABELS[status]}`,
                onDelete: () => handleChange({ target: { name: 'status', value: null } })
            });
        }
        return chips;
    }, [categoryMainId, categorySubId, isActive, status, categoryTree, handleChange]);

    return (
        <Box className={styles.wrapper}>
            <Box className={styles.inner}>
                <Box className={styles.header}>
                    <h2 className={styles.title}>FAQ 관리</h2>
                    <Stack direction='row' spacing={1}>
                        <Button
                            variant='outlined'
                            startIcon={bulkUploadLoading ? null : <UploadFileIcon />}
                            onClick={handleClickBulkUpload}
                            sx={{ width: 115.92 }}
                        >
                            {bulkUploadLoading ? <CircularProgress size={18} color='inherit' /> : '파일 업로드'}
                        </Button>
                        <Button
                            variant='contained'
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/admin/faq/new')}
                        >
                            신규 등록
                        </Button>
                    </Stack>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.xlsx,.xls,.csv'
                        style={{ display: 'none' }}
                        onChange={handleFileSelected}
                    />
                </Box>

                <Box className={styles.searchRow}>
                    <TextField
                        fullWidth
                        size='small'
                        placeholder='질문으로 검색'
                        name='keyword'
                        value={keyword || ''}
                        onChange={handleChange}
                        autoComplete='off'
                        inputProps={{ autoComplete: 'off' }}
                        InputProps={{ startAdornment: <HiOutlineSearch style={{ marginRight: 8, color: '#8a8a8a' }} /> }}
                    />
                </Box>

                <Box className={styles.filterPanel}>
                    <Box className={styles.filterRow}>
                        <CategoryFilter
                            mainId={categoryMainId}
                            subId={categorySubId}
                            tree={categoryTree}
                            onChange={handleCategoryChange}
                        />
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <Typography sx={inlineLabelSx}>노출 상태</Typography>
                            <Box sx={{ width: 140, flexShrink: 0 }}>
                                <TextField
                                    select
                                    size='small'
                                    fullWidth
                                    value={encodeActive(isActive)}
                                    onChange={handleActiveChange}
                                    sx={{ backgroundColor: '#fff' }}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: { sx: { maxWidth: 140, width: 140 } }
                                        }
                                    }}
                                >
                                    <MenuItem value={ACTIVE_ALL} sx={menuItemEllipsisSx}>전체</MenuItem>
                                    <MenuItem value={ACTIVE_TRUE} sx={menuItemEllipsisSx}>활성화</MenuItem>
                                    <MenuItem value={ACTIVE_FALSE} sx={menuItemEllipsisSx}>비활성화</MenuItem>
                                </TextField>
                            </Box>
                        </Stack>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <Typography sx={inlineLabelSx}>학습 상태</Typography>
                            <Box sx={{ width: 140, flexShrink: 0 }}>
                                <TextField
                                    select
                                    size='small'
                                    fullWidth
                                    value={encodeLearningStatus(status)}
                                    onChange={handleStatusChange}
                                    sx={{ backgroundColor: '#fff' }}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: { sx: { maxWidth: 140, width: 140 } }
                                        }
                                    }}
                                >
                                    <MenuItem value={LEARNING_STATUS_ALL} sx={menuItemEllipsisSx}>전체</MenuItem>
                                    <MenuItem value='PROGRESS' sx={menuItemEllipsisSx}>학습 중</MenuItem>
                                    <MenuItem value='COMPLETED' sx={menuItemEllipsisSx}>학습 완료</MenuItem>
                                    <MenuItem value='FAILURE' sx={menuItemEllipsisSx}>학습 실패</MenuItem>
                                </TextField>
                            </Box>
                        </Stack>
                    </Box>

                    <Box className={styles.filterDivider} />

                    <Stack direction='row' spacing={1} alignItems='center' className={styles.selectedFilterRow}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-darker, #222)', width: 80, flexShrink: 0, whiteSpace: 'nowrap' }}>  
                            선택된 필터 ({selectedChips.length})
                        </Typography>
                        <Tooltip title={selectedChips.length === 0 ? '' : '필터 초기화'}>
                            <span>
                                <IconButton
                                    size='small'
                                    onClick={handleResetFilters}
                                    disabled={selectedChips.length === 0}
                                    sx={{ p: 0.25 }}
                                >
                                    <RestartAltIcon fontSize='small' />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {selectedChips.map((chip) => (
                            <Chip
                                key={chip.key}
                                label={chip.label}
                                size='small'
                                variant='outlined'
                                onDelete={chip.onDelete}
                                deleteIcon={<CloseIcon />}
                                sx={{
                                    backgroundColor: '#fff',
                                    height: 28,
                                    px: 0.5,
                                    '& .MuiChip-label': { px: 1.25 },
                                    '& .MuiChip-deleteIcon': {
                                        fontSize: 16,
                                        color: 'rgba(0, 0, 0, 0.54)',
                                        backgroundColor: 'transparent'
                                    }
                                }}
                            />
                        ))}
                    </Stack>
                </Box>

                <FaqTable
                    listResponse={listResponse}
                    listLoading={listLoading}
                    listErrorResponse={listErrorResponse}
                    onDeleteRequest={handleDeleteRequest}
                    onRefreshList={onRefreshList}
                />

                {!listLoading && pagination && pagination.totalPages > 0 && (
                    <Pagination
                        prevPageNumberLength={prevPageNumberLength}
                        pageNumbers={pageNumbers}
                        currentPage={pagination.currentPage}
                        handleChangePage={requestPagination}
                        handleClickFunctionKeys={handleClickPaginationFunctionKeys}
                    />
                )}
            </Box>

            <Dialog
                open={!!pendingDelete}
                onClose={handleCloseDeleteModal}
                aria-labelledby='faq-delete-confirm-title'
            >
                <DialogTitle id='faq-delete-confirm-title'>
                    이 FAQ를 삭제하시겠습니까?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText component='div'>
                        <Box sx={{ mb: 1 }}>
                            삭제하면 복구할 수 없으며, 챗봇 응답에서도 제외됩니다.
                        </Box>
                        {pendingDelete && (
                            <Box
                                sx={{
                                    mt: 1,
                                    p: 1.5,
                                    borderRadius: 1,
                                    backgroundColor: '#f5f5f5',
                                    color: '#222',
                                    fontSize: 13,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {pendingDelete.question}
                            </Box>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteModal} variant='text' disabled={deleteLoading}>
                        취소
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant='contained'
                        color='error'
                        disabled={deleteLoading}
                        autoFocus
                    >
                        {deleteLoading ? <CircularProgress size={18} color='inherit' /> : '삭제'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={deleteErrorOpen}
                autoHideDuration={4000}
                onClose={() => { setDeleteErrorOpen(false); onDismissDeleteError && onDismissDeleteError(); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => { setDeleteErrorOpen(false); onDismissDeleteError && onDismissDeleteError(); }}
                    severity='error'
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.
                </Alert>
            </Snackbar>

            <Snackbar
                open={bulkUploadSnackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseBulkUploadSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseBulkUploadSnackbar}
                    severity={bulkUploadSnackbar.severity}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {bulkUploadSnackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
